import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CatalogFieldMappingConfig, FieldMapping } from '@nousmedis/cc-types';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { FieldMappingService } from '../../../services/field-mapping.service';

@Component({
  selector: 'app-field-mapping-editor',
  templateUrl: './field-mapping-editor.component.html',
})
export class FieldMappingEditorComponent {
  @Input() idCatalog!: string;
  @Output() saved = new EventEmitter<CatalogFieldMappingConfig>();

  productFields: string[] = [];
  categoryFields: string[] = [];

  mappingProducts: FieldMapping[] = [];
  mappingCategories: FieldMapping[] = [];

  loading = false;
  saving = false;


  constructor(
    private fieldMappingService: FieldMappingService,
    private messageService: MessageService,
  ) {}

  async load(): Promise<void> {
    this.loading = true;
    this.productFields = [];
    this.categoryFields = [];
    this.mappingProducts = [];
    this.mappingCategories = [];

    try {
      const [preview, existing] = await Promise.all([
        this.fieldMappingService.getImportPreview(this.idCatalog),
        firstValueFrom(this.fieldMappingService.getFieldMapping(this.idCatalog)),
      ]);

      this.productFields = preview.productFields;
      this.categoryFields = preview.categoryFields;

      const savedProducts: FieldMapping[] = existing?.products ?? [];
      const savedCategories: FieldMapping[] = existing?.categories ?? [];

      this.mappingProducts = this.productFields.map(field => {
        const saved = savedProducts.find(m => m.sourceField === field);
        const autoTarget = this.productFields.includes(field) ? field : '';
        return saved ?? { sourceField: field, targetField: autoTarget };
      });

      this.mappingCategories = this.categoryFields.map(field => {
        const saved = savedCategories.find(m => m.sourceField === field);
        const autoTarget = this.categoryFields.includes(field) ? field : '';
        return saved ?? { sourceField: field, targetField: autoTarget };
      });
    } catch {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'No se pudo cargar la preview de Syncwall. Puede configurar el mapping manualmente.',
      });
    } finally {
      this.loading = false;
    }
  }

  addProductMapping(): void {
    this.mappingProducts = [...this.mappingProducts, { sourceField: '', targetField: '' }];
  }

  removeProductMapping(index: number): void {
    this.mappingProducts = this.mappingProducts.filter((_, i) => i !== index);
  }

  addCategoryMapping(): void {
    this.mappingCategories = [...this.mappingCategories, { sourceField: '', targetField: '' }];
  }

  removeCategoryMapping(index: number): void {
    this.mappingCategories = this.mappingCategories.filter((_, i) => i !== index);
  }

  async save(): Promise<void> {
    const config: CatalogFieldMappingConfig = {
      idCatalog: this.idCatalog,
      products: this.mappingProducts.filter(m => m.sourceField && m.targetField),
      categories: this.mappingCategories.filter(m => m.sourceField && m.targetField),
    };

    this.saving = true;
    try {
      await firstValueFrom(this.fieldMappingService.saveFieldMapping(this.idCatalog, config));
      this.messageService.add({
        severity: 'success',
        summary: 'Guardado',
        detail: 'Configuración de mapping guardada',
      });
      this.saved.emit(config);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo guardar el mapping',
      });
    } finally {
      this.saving = false;
    }
  }

  toFieldOptions(fields: string[]) {
    return fields.map(f => ({ label: f, value: f }));
  }
}
